import {
  closestCenter,
  DndContext,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type Announcements,
  type DragEndEvent,
  type Modifier,
} from '@dnd-kit/core';
import { horizontalListSortingStrategy, SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight, ImageOff, Plus, X } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import { SummaryCard } from '@nucleus-commerce/admin-kit/forms';
import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import {
  ProductRelationRelationType,
  type Product,
  type ProductListItem,
  type ProductRelation,
} from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { ProductPicker } from '@/features/resource-pickers';

import { useProductRelations, useSetProductRelations } from '../hooks/use-product-relations';
import { useProductScope } from '../hooks/use-product-scope';
import { productListQueryOptions } from '../hooks/use-products';
import { useRowOverflow } from '../hooks/use-row-overflow';

import { SharedChip } from './product-scope';
import { ProductStatusBadge } from './product-status-badge';

export interface RelationsSectionProps {
  product: Product;
}

type RelationType = ProductRelation['relationType'];
type Image = ProductListItem['featuredImage'];

/** Upsell first: the order the rows always show in, the API's own. */
const TYPES = Object.values(ProductRelationRelationType);

/** Related products of every type together: the PUT takes at most this many. */
const MAX_RELATIONS = 100;

/** The Add tile's room at the row's end, which a page of tiles must not count on. */
const ADD_TILE_PX = 92;

/** The same product can sit in two types, so a tile's id carries both. */
const tileId = (row: Pick<ProductRelation, 'relationType' | 'relatedProductId'>) =>
  `${row.relationType}:${row.relatedProductId}`;

const alongTheRow: Modifier = ({ transform }) => ({ ...transform, y: 0 });

const RING = 'focus-visible:ring-ring/50 outline-none focus-visible:ring-[3px]';

interface RowEditing {
  productId: string;
  /** How many more the product can take across every type. */
  room: number;
  /** The reason Add is off, by id, when the product is full. */
  fullNoteId: string;
  /** Saved in this type: ticked and locked in the picker. */
  existing: ReadonlySet<string>;
  onAdd: (ids: string[]) => void;
  onRemove: (row: ProductRelation) => void;
  /** False when nothing moved: at the row's end, or while a save runs. */
  onMove: (from: number, to: number) => boolean;
}

function TileImage({ image }: { image: Image | undefined }) {
  return image ? (
    <img
      src={image.url}
      alt=""
      draggable={false}
      loading="lazy"
      className="size-full object-cover"
    />
  ) : (
    <span className="text-muted-foreground flex size-full items-center justify-center">
      <ImageOff aria-hidden className="size-4" />
    </span>
  );
}

function Tile({
  row,
  image,
  position,
  count,
  onMove,
  onRemove,
}: {
  row: ProductRelation;
  image: Image | undefined;
  position: number;
  count: number;
  /** Absent on a page that only reads, with `onRemove`. */
  onMove?: ((delta: -1 | 1) => void) | undefined;
  onRemove?: (() => void) | undefined;
}) {
  const { t } = useTranslation('products');
  const movable = onMove !== undefined && count > 1;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: tileId(row),
    disabled: !movable,
  });
  const title = row.product.title;
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    onMove?.(e.key === 'ArrowLeft' ? -1 : 1);
  };
  const frame = 'bg-muted block aspect-square w-full overflow-hidden rounded-md border';
  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={cn(
        'relative w-20 shrink-0 snap-start',
        isDragging && 'bg-card z-[5] rounded-lg shadow-lg',
      )}
    >
      <div className="relative">
        {movable ? (
          // Only the picture drags: a drag that began on the title would end by following its link.
          <button
            type="button"
            {...attributes}
            {...listeners}
            data-tile={tileId(row)}
            aria-label={t('relatedCard.tileLabel', {
              type: t(`relations.type.${row.relationType}`),
              title,
              position,
              count,
            })}
            onKeyDown={onKeyDown}
            className={cn(frame, RING, 'cursor-grab touch-manipulation active:cursor-grabbing')}
          >
            <TileImage image={image} />
          </button>
        ) : (
          <div className={frame}>
            <TileImage image={image} />
          </div>
        )}
        {row.product.status === 'active' ? null : (
          <ProductStatusBadge
            status={row.product.status}
            className="bg-background pointer-events-none absolute bottom-0.5 left-0.5 h-4 px-1 text-[10px]"
          />
        )}
        {onRemove ? (
          <button
            type="button"
            aria-label={t('relations.remove', { title })}
            onClick={onRemove}
            className={cn(
              RING,
              'bg-background/90 text-muted-foreground hover:text-foreground absolute top-0.5 right-0.5 rounded-full border p-px',
            )}
          >
            <X className="size-3" aria-hidden />
          </button>
        ) : null}
      </div>
      <Link
        to="/org/products/$productId"
        params={{ productId: row.relatedProductId }}
        title={title}
        className={cn(RING, 'mt-1 block truncate rounded-sm text-xs leading-4 hover:underline')}
      >
        {title}
      </Link>
    </li>
  );
}

function AddTile({
  type,
  editing,
  softEdge,
}: {
  type: RelationType;
  editing: RowEditing;
  /** Tiles pass under it, so its edge fades into them. */
  softEdge: boolean;
}) {
  const { t } = useTranslation('products');
  const [open, setOpen] = useState(false);
  const exclude = useMemo(() => new Set([editing.productId]), [editing.productId]);
  const full = editing.room <= 0;
  const title = t(`relatedCard.pickerTitle.${type}`);
  return (
    <li
      className={cn(
        'bg-card sticky right-0 z-10 flex shrink-0 items-start self-stretch',
        softEdge &&
          'before:from-card pr-1 pl-2 before:pointer-events-none before:absolute before:inset-y-0 before:-left-4 before:w-4 before:bg-linear-to-l before:to-transparent',
      )}
    >
      <button
        type="button"
        disabled={full}
        aria-label={title}
        aria-describedby={full ? editing.fullNoteId : undefined}
        title={full ? t('relations.full', { max: MAX_RELATIONS }) : undefined}
        onClick={() => setOpen(true)}
        className={cn(
          RING,
          'text-muted-foreground enabled:hover:border-primary/60 enabled:hover:text-foreground flex size-20 flex-col items-center justify-center gap-0.5 rounded-md border border-dashed text-xs disabled:cursor-not-allowed disabled:opacity-50',
        )}
      >
        <Plus className="size-3.5" aria-hidden />
        {t('relatedCard.add')}
      </button>
      {full ? null : (
        <ProductPicker
          open={open}
          onOpenChange={setOpen}
          scope={{ kind: 'org' }}
          title={title}
          multiple={editing.room}
          lockedIds={editing.existing}
          excludeIds={exclude}
          onConfirm={(selection) =>
            editing.onAdd(selection.ids.filter((id) => !editing.existing.has(id)))
          }
        />
      )}
    </li>
  );
}

function RelationRow({
  type,
  rows,
  images,
  editing,
}: {
  type: RelationType;
  rows: readonly ProductRelation[];
  images: ReadonlyMap<string, Image>;
  /** Null on a page that only reads. */
  editing: RowEditing | null;
}) {
  const { t } = useTranslation('products');
  const headingId = useId();
  const label = t(`relations.type.${type}`);
  const { ref: list, overflowing, canPrevious, canNext, page } = useRowOverflow<HTMLUListElement>();
  const covered = editing ? ADD_TILE_PX : 0;

  // A tile moved by the arrow keys lands somewhere else in the DOM, which can drop its focus.
  const refocus = useRef<string | null>(null);
  const [moved, setMoved] = useState('');
  useEffect(() => {
    if (!refocus.current) return;
    const tiles = list.current?.querySelectorAll<HTMLElement>('[data-tile]') ?? [];
    for (const tile of tiles) {
      if (tile.dataset.tile === refocus.current) {
        tile.focus();
        tile.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    }
    refocus.current = null;
  }, [rows, list]);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    // Touch waits for a press, so a swipe on a picture still scrolls the row.
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 6 } }),
  );
  const titleOf = (id: string | number) =>
    rows.find((row) => tileId(row) === id)?.product.title ?? '';
  const announcements: Announcements = {
    onDragStart: ({ active }) => t('relatedCard.dnd.picked', { title: titleOf(active.id) }),
    onDragOver: () => undefined,
    onDragEnd: ({ active, over }) =>
      over
        ? t('relatedCard.dnd.dropped', { title: titleOf(active.id) })
        : t('relatedCard.dnd.cancelled', { title: titleOf(active.id) }),
    onDragCancel: ({ active }) => t('relatedCard.dnd.cancelled', { title: titleOf(active.id) }),
  };
  // Snapping would pull against the auto-scroll at the row's edges while a tile is carried.
  const [dragging, setDragging] = useState(false);
  const onDragEnd = ({ active, over }: DragEndEvent) => {
    setDragging(false);
    if (!over || active.id === over.id) return;
    const from = rows.findIndex((row) => tileId(row) === active.id);
    const to = rows.findIndex((row) => tileId(row) === over.id);
    if (from >= 0 && to >= 0) editing?.onMove(from, to);
  };

  const tiles = rows.map((row, index) => (
    <Tile
      key={tileId(row)}
      row={row}
      image={images.get(row.relatedProductId)}
      position={index + 1}
      count={rows.length}
      onMove={
        editing
          ? (delta) => {
              if (!editing.onMove(index, index + delta)) return;
              refocus.current = tileId(row);
              setMoved(
                t('relatedCard.dnd.moved', {
                  title: row.product.title,
                  position: index + 1 + delta,
                  count: rows.length,
                }),
              );
            }
          : undefined
      }
      onRemove={editing ? () => editing.onRemove(row) : undefined}
    />
  ));

  const scroller = (
    <ul
      ref={list}
      aria-labelledby={headingId}
      className={cn(
        // Room for the focus rings the scroller clips, except beside Add: a sticky tile stops at the
        // padding, so tiles would show through to its right.
        '-mx-1 flex min-w-0 scroll-pl-1 [scrollbar-width:thin] [scrollbar-color:var(--border)_transparent] gap-1.5 overflow-x-auto py-1 pb-1.5 pl-1',
        editing ? 'scroll-pr-20' : 'pr-1',
        !dragging && 'snap-x snap-mandatory',
      )}
    >
      {tiles}
      {/* Right after the last product; `sticky` holds it at the edge once the row scrolls. */}
      {editing ? <AddTile type={type} editing={editing} softEdge={overflowing} /> : null}
      {rows.length === 0 ? (
        <li className="text-muted-foreground flex h-20 items-center px-1 text-xs">
          {t('relatedCard.none')}
        </li>
      ) : null}
    </ul>
  );

  return (
    <section aria-labelledby={headingId} className="flex min-w-0 flex-col">
      <div className="flex min-h-5 items-center gap-2">
        <h3 id={headingId} className="text-[13px] font-medium">
          {t('relatedCard.rowLabel', { type: label, count: rows.length })}
        </h3>
        {overflowing ? (
          <div className="ml-auto flex gap-1">
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              className="size-5"
              disabled={!canPrevious}
              aria-label={t('relatedCard.previous', { type: label })}
              onClick={() => page(-1, covered)}
            >
              <ChevronLeft aria-hidden />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              className="size-5"
              disabled={!canNext}
              aria-label={t('relatedCard.next', { type: label })}
              onClick={() => page(1, covered)}
            >
              <ChevronRight aria-hidden />
            </Button>
          </div>
        ) : null}
      </div>
      {editing ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[alongTheRow]}
          onDragStart={() => setDragging(true)}
          onDragCancel={() => setDragging(false)}
          onDragEnd={onDragEnd}
          accessibility={{
            announcements,
            screenReaderInstructions: { draggable: t('relatedCard.dnd.instructions') },
          }}
        >
          <SortableContext items={rows.map(tileId)} strategy={horizontalListSortingStrategy}>
            {scroller}
          </SortableContext>
          <span className="sr-only" aria-live="polite">
            {moved}
          </span>
        </DndContext>
      ) : (
        scroller
      )}
    </section>
  );
}

/** Upsell, cross-sell, matching and similar as four rows of tiles. Each change saves on its own. */
export function RelationsSection({ product }: RelationsSectionProps) {
  const { t } = useTranslation('products');
  const { can } = usePermissions();
  const { readOnly } = useProductScope();
  const canEdit = !readOnly && can('products.write');
  const fullNoteId = useId();
  const query = useProductRelations(product.id);
  const setRelations = useSetProductRelations(product.id);
  const relations = useMemo(() => query.data ?? [], [query.data]);

  // Relation rows carry no image; the list answers with every featured image in one request.
  const relatedIds = [...new Set(relations.map((r) => r.relatedProductId))].sort();
  const pictures = useQuery({
    ...productListQueryOptions({ ids: relatedIds.join(','), limit: MAX_RELATIONS }),
    enabled: relatedIds.length > 0,
  });
  const images = useMemo(
    () => new Map((pictures.data?.items ?? []).map((item) => [item.id, item.featuredImage])),
    [pictures.data],
  );

  // By position inside a type. While a PUT runs its own order shows, so a dropped tile doesn't snap
  // back until the answer lands; a failure shows the saved order.
  const sent = setRelations.isPending ? setRelations.variables.items : null;
  const groups = useMemo(() => {
    const sorted = [...relations].sort((a, b) => a.position - b.position);
    const shown = sent
      ? sent.flatMap((item) =>
          relations.filter(
            (r) =>
              r.relatedProductId === item.relatedProductId && r.relationType === item.relationType,
          ),
        )
      : sorted;
    return TYPES.map((type) => ({
      type,
      rows: shown.filter((r) => r.relationType === type),
    }));
  }, [relations, sent]);

  const save = (next: ReadonlyArray<Pick<ProductRelation, 'relatedProductId' | 'relationType'>>) =>
    setRelations.mutate({
      items: next.map(({ relatedProductId, relationType }) => ({ relatedProductId, relationType })),
    });

  // Order inside a type is the display order the PUT persists; other types keep their order.
  const move = (type: RelationType, from: number, to: number) => {
    if (setRelations.isPending) return false;
    const ofType = [...(groups.find((g) => g.type === type)?.rows ?? [])];
    if (to < 0 || to >= ofType.length) return false;
    const [row] = ofType.splice(from, 1);
    if (!row) return false;
    ofType.splice(to, 0, row);
    save([...relations.filter((r) => r.relationType !== type), ...ofType]);
    return true;
  };

  const editingFor = (type: RelationType): RowEditing | null =>
    canEdit
      ? {
          productId: product.id,
          room: MAX_RELATIONS - relations.length,
          fullNoteId,
          existing: new Set(
            relations.filter((r) => r.relationType === type).map((r) => r.relatedProductId),
          ),
          onAdd: (ids) =>
            save([
              ...relations,
              ...ids.map((relatedProductId) => ({ relatedProductId, relationType: type })),
            ]),
          onRemove: (row) =>
            save(
              relations.filter(
                (r) => !(r.relationType === type && r.relatedProductId === row.relatedProductId),
              ),
            ),
          onMove: (from, to) => move(type, from, to),
        }
      : null;

  let body;
  if (query.isPending) {
    body = TYPES.map((type) => <Skeleton key={type} className="h-28 w-full rounded-md" />);
  } else if (query.error) {
    body = <ErrorState error={query.error} onRetry={() => void query.refetch()} compact />;
  } else {
    body = groups.map(({ type, rows }) => (
      <RelationRow key={type} type={type} rows={rows} images={images} editing={editingFor(type)} />
    ));
  }

  return (
    <SummaryCard title={t('relations.title')} badge={<SharedChip />}>
      <div className="flex min-w-0 flex-col gap-2">{body}</div>
      {canEdit && relations.length >= MAX_RELATIONS ? (
        <p id={fullNoteId} className="text-muted-foreground text-xs">
          {t('relations.full', { max: MAX_RELATIONS })}
        </p>
      ) : null}
    </SummaryCard>
  );
}
